import { DomainLandSituationType } from "@porchpass/api-client";
import type { LandFieldsFormData } from "@porchpass/ui";
import type {
  LandAlreadyOwnedFormData,
  LandCurrentlyFinancedFormData,
  LandFormData,
  LandLeasingFormData,
  LandPurchasingFormData,
} from "src/types/land";

const LAND_SITUATION_TO_DOMAIN: Record<DomainLandSituationType, string> = {
  ALREADY_OWNED: DomainLandSituationType.LandSituationTypeAlreadyOwned,
  CURRENTLY_FINANCED:
    DomainLandSituationType.LandSituationTypeCurrentlyFinanced,
  PURCHASING: DomainLandSituationType.LandSituationTypePurchasing,
  LEASING: DomainLandSituationType.LandSituationTypeLeasing,
  FAMILY: DomainLandSituationType.LandSituationTypeFamily,
  RESERVATION: DomainLandSituationType.LandSituationReservation,
};

function parseOptionalNumber(value?: string): number | undefined {
  if (value == null || value === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

export function toLandFieldsFormData(dto: LandFormData): LandFieldsFormData {
  return {
    landSituation: (dto.land_situation_type
      ? LAND_SITUATION_TO_DOMAIN[dto.land_situation_type]
      : "") as LandFieldsFormData["landSituation"],
    useAsDownPayment: dto.use_as_down_payment,
    estimatedLandValue: parseOptionalNumber(dto.land_value),
    acreage: parseOptionalNumber(dto.acreage),
    purchaseDateMonth: dto.purchase_month,
    purchaseDateYear: dto.purchase_year,
    hasFrontagePublicRoad: dto.has_frontage_public_road,
    hasPavedRoad: dto.has_paved_road,
    hoaFeeAmount: parseOptionalNumber(dto.hoa_fee_amount),
    monthlyLandPayment: parseOptionalNumber(dto.monthly_payment),
    existingMortgageBalance: parseOptionalNumber(dto.purchase_price_payoff),
    isRefinance: dto.is_refinance,
    landlordName: dto.land_owner,
    leaseType: dto.lease_type,
    monthlyLeasePayment: parseOptionalNumber(dto.monthly_payment),
    // "No" is saved as an empty explanation (DEA-962), "Yes" with its text
    hasPriceIncrease:
      dto.price_increase_explanation == null
        ? undefined
        : dto.price_increase_explanation.trim() !== "",
    priceIncreaseExplanation: dto.price_increase_explanation,
  };
}

export function fromLandAlreadyOwnedFormData(
  data: LandFieldsFormData,
): LandAlreadyOwnedFormData {
  return {
    use_as_down_payment: data.useAsDownPayment,
    land_value:
      data.estimatedLandValue != null
        ? String(data.estimatedLandValue)
        : undefined,
    acreage: data.acreage != null ? String(data.acreage) : undefined,
    purchase_month: data.purchaseDateMonth,
    purchase_year: data.purchaseDateYear,
    has_frontage_public_road: data.hasFrontagePublicRoad,
    has_paved_road: data.hasPavedRoad,
    hoa_fee_amount:
      data.hoaFeeAmount != null ? String(data.hoaFeeAmount) : undefined,
  };
}

export function fromLandFinancedFormData(
  data: LandFieldsFormData,
): LandCurrentlyFinancedFormData {
  return {
    monthly_payment:
      data.monthlyLandPayment != null
        ? String(data.monthlyLandPayment)
        : undefined,
    use_as_down_payment: data.useAsDownPayment,
    is_refinance: data.isRefinance,
    purchase_price_payoff:
      data.existingMortgageBalance != null
        ? String(data.existingMortgageBalance)
        : undefined,
    land_value:
      data.estimatedLandValue != null
        ? String(data.estimatedLandValue)
        : undefined,
    acreage: data.acreage != null ? String(data.acreage) : undefined,
    purchase_month: data.purchaseDateMonth,
    purchase_year: data.purchaseDateYear,
    land_owner: data.landlordName,
    has_frontage_public_road: data.hasFrontagePublicRoad,
    has_paved_road: data.hasPavedRoad,
    hoa_fee_amount:
      data.hoaFeeAmount != null ? String(data.hoaFeeAmount) : undefined,
  };
}

export function fromLandPurchasingFormData(
  data: LandFieldsFormData,
): LandPurchasingFormData {
  return {
    purchase_price_payoff:
      data.existingMortgageBalance != null
        ? String(data.existingMortgageBalance)
        : undefined,
    acreage: data.acreage != null ? String(data.acreage) : undefined,
    land_owner: data.landlordName,
    has_frontage_public_road: data.hasFrontagePublicRoad,
    has_paved_road: data.hasPavedRoad,
    hoa_fee_amount:
      data.hoaFeeAmount != null ? String(data.hoaFeeAmount) : undefined,
  };
}

export function fromLandLeasingFormData(
  data: LandFieldsFormData,
): LandLeasingFormData {
  return {
    hoa_fee_amount:
      data.hoaFeeAmount != null ? String(data.hoaFeeAmount) : undefined,
    land_owner: data.landlordName,
    lease_type: data.leaseType,
    monthly_payment:
      data.monthlyLeasePayment != null
        ? String(data.monthlyLeasePayment)
        : undefined,
    has_price_increase: data.hasPriceIncrease,
    price_increase_explanation: data.priceIncreaseExplanation,
  };
}

/**
 * Combines the saved land with edits the borrower made while it was loading.
 * Fields they touched keep their value; the rest come from the saved land. If
 * they picked a different situation, the saved details belong to another
 * situation, so their form is kept as is.
 */
export function mergeSavedLandFields(
  saved: LandFieldsFormData,
  current: LandFieldsFormData,
  editedFields: ReadonlySet<keyof LandFieldsFormData>,
): LandFieldsFormData {
  if (current.landSituation && current.landSituation !== saved.landSituation) {
    return current;
  }
  const edits = Object.fromEntries(
    [...editedFields].map((key) => [key, current[key]]),
  ) as Partial<LandFieldsFormData>;
  return { ...saved, ...edits };
}
